import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { GuestRequest } from '../../types';

const POSITIONS = ['Arquero', 'Defensor', 'Mediocampista', 'Delantero'];

interface GuestEntry {
  id: string;
  guestName: string;
  guestPosition: string;
  guestRating: number;
}

let nextEntryId = 1;
const createEntry = (): GuestEntry => ({
  id: String(nextEntryId++),
  guestName: '',
  guestPosition: 'Mediocampista',
  guestRating: 3,
});

interface ManageGuestsModalProps {
  isOpen: boolean;
  matchId: string;
  isOrganizer: boolean;
  onClose: () => void;
  onGuestAdded: () => void;
  onShowNotification: (msg: string, type: 'success' | 'error') => void;
}

type TabType = 'propose' | 'direct';

// ---- Star rating component ----
const StarRating: React.FC<{
  rating: number;
  onChange: (v: number) => void;
}> = ({ rating, onChange }) => (
  <div style={{ display: 'flex', gap: '4px' }}>
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '26px', color: star <= rating ? '#FFD700' : '#555',
          padding: '0 2px', lineHeight: 1,
        }}
      >
        {star <= rating ? '★' : '☆'}
      </button>
    ))}
  </div>
);

// ---- Entry form ----
const EntryForm: React.FC<{
  entry: GuestEntry;
  index: number;
  total: number;
  onUpdate: (id: string, field: keyof GuestEntry, value: string | number) => void;
  onRemove: (id: string) => void;
}> = ({ entry, index, total, onUpdate, onRemove }) => (
  <div style={{
    backgroundColor: '#1e2124',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid #33383B',
  }}>
    {total > 1 && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '12px', color: '#B0BEC5', fontWeight: 600 }}>
          Invitado {index + 1}
        </span>
        <button
          type="button"
          onClick={() => onRemove(entry.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0BEC5', fontSize: '16px', padding: '0' }}
        >
          ✕
        </button>
      </div>
    )}

    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '12px', color: '#B0BEC5', fontWeight: 600, marginBottom: '6px' }}>
        Nombre
      </label>
      <input
        type="text"
        placeholder="Nombre del invitado"
        value={entry.guestName}
        onChange={e => onUpdate(entry.id, 'guestName', e.target.value)}
        maxLength={50}
        style={{
          width: '100%',
          backgroundColor: '#2a2d2f',
          border: '1px solid #33383B',
          borderRadius: '8px',
          padding: '10px 12px',
          color: '#FFFFFF',
          fontSize: '14px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>

    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '12px', color: '#B0BEC5', fontWeight: 600, marginBottom: '6px' }}>
        Posición
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {POSITIONS.map(pos => (
          <button
            key={pos}
            type="button"
            onClick={() => onUpdate(entry.id, 'guestPosition', pos)}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              border: `1px solid ${entry.guestPosition === pos ? '#00E676' : '#33383B'}`,
              backgroundColor: entry.guestPosition === pos ? '#00E676' : '#2a2d2f',
              color: entry.guestPosition === pos ? '#000' : '#B0BEC5',
              fontSize: '13px',
              fontWeight: entry.guestPosition === pos ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {pos}
          </button>
        ))}
      </div>
    </div>

    <div>
      <label style={{ display: 'block', fontSize: '12px', color: '#B0BEC5', fontWeight: 600, marginBottom: '6px' }}>
        Nivel
      </label>
      <StarRating rating={entry.guestRating} onChange={v => onUpdate(entry.id, 'guestRating', v)} />
    </div>
  </div>
);

// ---- ProposeTab ----
const ProposeTab: React.FC<{
  matchId: string;
  onGuestProposed: () => void;
  onShowNotification: (msg: string, type: 'success' | 'error') => void;
}> = ({ matchId, onGuestProposed, onShowNotification }) => {
  const [entries, setEntries] = useState<GuestEntry[]>([createEntry()]);
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState<GuestRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchMyRequests = async () => {
    setLoadingRequests(true);
    const response = await apiRequest<GuestRequest[]>(`/api/matches/${matchId}/guest-requests`);
    if (response.data) {
      setMyRequests(response.data.filter(r => r.status !== 'PENDING'));
    }
    setLoadingRequests(false);
  };

  useEffect(() => {
    fetchMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  const updateEntry = (id: string, field: keyof GuestEntry, value: string | number) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const addEntry = () => setEntries(prev => [...prev, createEntry()]);

  const removeEntry = (id: string) => {
    if (entries.length === 1) return;
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleSubmit = async () => {
    const validEntries = entries.filter(e => e.guestName.trim());
    if (validEntries.length === 0) {
      setErrorMsg('Ingresá al menos un nombre');
      return;
    }
    setErrorMsg(null);
    setSubmitting(true);
    const errors: string[] = [];
    let successCount = 0;

    for (const entry of validEntries) {
      const response = await apiRequest(`/api/matches/${matchId}/guest-requests`, {
        method: 'POST',
        body: {
          guestName: entry.guestName.trim(),
          guestPosition: entry.guestPosition,
          guestRating: entry.guestRating,
        },
      });
      if (response.error) {
        errors.push(`${entry.guestName}: ${response.error}`);
      } else {
        successCount++;
      }
    }

    setSubmitting(false);

    if (errors.length > 0) {
      setErrorMsg(errors.join('\n'));
    }
    if (successCount > 0) {
      setEntries([createEntry()]);
      fetchMyRequests();
      onGuestProposed();
      if (errors.length === 0) {
        onShowNotification(
          successCount > 1
            ? `${successCount} solicitudes enviadas. El organizador va a revisar.`
            : 'Solicitud enviada. El organizador va a revisar tu propuesta.',
          'success'
        );
      }
    }
  };

  const handleShare = async (request: GuestRequest) => {
    // Cache-buster para que WhatsApp re-escrapee el preview con datos actuales.
    const shareLink = `${window.location.origin}/join/${matchId}?t=${Date.now()}`;
    try {
      await navigator.clipboard.writeText(shareLink);
      onShowNotification(`Link copiado para compartir con ${request.guestName}`, 'success');
    } catch {
      onShowNotification('No se pudo copiar el link', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'APPROVED') return '#10B981';
    if (status === 'REJECTED') return '#EF4444';
    return '#F59E0B';
  };

  const getStatusLabel = (status: string) => {
    if (status === 'APPROVED') return 'Aprobado';
    if (status === 'REJECTED') return 'Rechazado';
    return 'Pendiente';
  };

  const validCount = entries.filter(e => e.guestName.trim()).length;

  return (
    <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#B0BEC5' }}>
        El organizador va a aprobar o rechazar tus solicitudes
      </p>

      {entries.map((entry, index) => (
        <EntryForm
          key={entry.id}
          entry={entry}
          index={index}
          total={entries.length}
          onUpdate={updateEntry}
          onRemove={removeEntry}
        />
      ))}

      <button
        type="button"
        onClick={addEntry}
        style={{
          width: '100%',
          padding: '10px',
          border: '1px dashed #00E676',
          borderRadius: '10px',
          backgroundColor: 'transparent',
          color: '#00E676',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
        }}
      >
        + Agregar otro jugador
      </button>

      {errorMsg && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid #EF4444',
          borderRadius: '8px', padding: '10px 14px', marginBottom: '12px',
          color: '#EF4444', fontSize: '13px', whiteSpace: 'pre-line',
        }}>
          {errorMsg}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={validCount === 0 || submitting}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#00E676',
          color: '#000',
          border: 'none',
          borderRadius: '10px',
          fontSize: '15px',
          fontWeight: 700,
          cursor: validCount === 0 || submitting ? 'not-allowed' : 'pointer',
          opacity: validCount === 0 || submitting ? 0.5 : 1,
          marginBottom: '20px',
        }}
      >
        {submitting
          ? 'Enviando...'
          : validCount > 1
            ? `Proponer ${validCount} invitados`
            : 'Proponer invitado'}
      </button>

      {/* Mis solicitudes */}
      {!loadingRequests && myRequests.length > 0 && (
        <div style={{ borderTop: '1px solid #33383B', paddingTop: '16px', marginBottom: '16px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: '#FFFFFF' }}>
            Mis solicitudes
          </h3>
          {myRequests.map(request => (
            <div key={request.id} style={{
              backgroundColor: '#1e2124',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '8px',
              border: '1px solid #33383B',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                  {request.guestName}
                </span>
                <span style={{
                  backgroundColor: getStatusColor(request.status) + '20',
                  color: getStatusColor(request.status),
                  borderRadius: '6px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  {getStatusLabel(request.status)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '12px', color: '#B0BEC5' }}>{request.guestPosition}</span>
                <span style={{ fontSize: '11px', color: '#FFD700' }}>
                  {'★'.repeat(request.guestRating)}{'☆'.repeat(5 - request.guestRating)}
                </span>
              </div>
              {request.status === 'APPROVED' && (
                <button
                  type="button"
                  onClick={() => handleShare(request)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#00E676', fontSize: '13px', fontWeight: 500,
                    padding: '6px 0 0', display: 'flex', alignItems: 'center', gap: '4px',
                    borderTop: '1px solid #33383B', marginTop: '8px', width: '100%',
                  }}
                >
                  📤 Compartir con {request.guestName}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- DirectTab ----
const DirectTab: React.FC<{
  matchId: string;
  onGuestAdded: () => void;
  onShowNotification: (msg: string, type: 'success' | 'error') => void;
}> = ({ matchId, onGuestAdded, onShowNotification }) => {
  const [entries, setEntries] = useState<GuestEntry[]>([createEntry()]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const updateEntry = (id: string, field: keyof GuestEntry, value: string | number) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const addEntry = () => setEntries(prev => [...prev, createEntry()]);

  const removeEntry = (id: string) => {
    if (entries.length === 1) return;
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleSubmit = async () => {
    const validEntries = entries.filter(e => e.guestName.trim());
    if (validEntries.length === 0) {
      setErrorMsg('Ingresá al menos un nombre');
      return;
    }
    setErrorMsg(null);
    setSubmitting(true);
    const errors: string[] = [];
    let successCount = 0;

    for (const entry of validEntries) {
      const response = await apiRequest(`/api/matches/${matchId}/add-guest`, {
        method: 'POST',
        body: {
          guestName: entry.guestName.trim(),
          guestPosition: entry.guestPosition,
          guestRating: entry.guestRating,
        },
      });
      if (response.error) {
        errors.push(`${entry.guestName}: ${response.error}`);
      } else {
        successCount++;
      }
    }

    setSubmitting(false);

    if (errors.length > 0) {
      setErrorMsg(errors.join('\n'));
    }
    if (successCount > 0) {
      setEntries([createEntry()]);
      onGuestAdded();
      if (errors.length === 0) {
        onShowNotification(
          successCount > 1 ? `${successCount} invitados agregados al partido` : 'Invitado agregado al partido',
          'success'
        );
      }
    }
  };

  const validCount = entries.filter(e => e.guestName.trim()).length;

  return (
    <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#B0BEC5' }}>
        Los invitados se agregan directamente al partido sin necesitar aprobación
      </p>

      {entries.map((entry, index) => (
        <EntryForm
          key={entry.id}
          entry={entry}
          index={index}
          total={entries.length}
          onUpdate={updateEntry}
          onRemove={removeEntry}
        />
      ))}

      <button
        type="button"
        onClick={addEntry}
        style={{
          width: '100%',
          padding: '10px',
          border: '1px dashed #00E676',
          borderRadius: '10px',
          backgroundColor: 'transparent',
          color: '#00E676',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
        }}
      >
        + Agregar otro jugador
      </button>

      {errorMsg && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid #EF4444',
          borderRadius: '8px', padding: '10px 14px', marginBottom: '12px',
          color: '#EF4444', fontSize: '13px', whiteSpace: 'pre-line',
        }}>
          {errorMsg}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={validCount === 0 || submitting}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#00E676',
          color: '#000',
          border: 'none',
          borderRadius: '10px',
          fontSize: '15px',
          fontWeight: 700,
          cursor: validCount === 0 || submitting ? 'not-allowed' : 'pointer',
          opacity: validCount === 0 || submitting ? 0.5 : 1,
          marginBottom: '16px',
        }}
      >
        {submitting
          ? 'Agregando...'
          : validCount > 1
            ? `Agregar ${validCount} invitados`
            : 'Agregar invitado'}
      </button>
    </div>
  );
};

// ---- Main modal ----
export const ManageGuestsModal: React.FC<ManageGuestsModalProps> = ({
  isOpen,
  matchId,
  isOrganizer,
  onClose,
  onGuestAdded,
  onShowNotification,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('propose');

  useEffect(() => {
    if (isOpen) {
      setActiveTab('propose');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string }[] = [
    { id: 'propose', label: 'Proponer invitado' },
    ...(isOrganizer ? [{ id: 'direct' as TabType, label: 'Agregar directo' }] : []),
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '16px',
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '18px',
        border: '1px solid #33383B',
        padding: '24px',
        width: '100%',
        maxWidth: '520px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#FFFFFF' }}>
            Invitados
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#B0BEC5', fontSize: '20px', lineHeight: 1, padding: '4px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        {isOrganizer && (
          <div style={{
            display: 'flex', gap: '8px', marginBottom: '20px',
            backgroundColor: '#1e2124', borderRadius: '10px', padding: '4px',
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1, padding: '8px',
                  borderRadius: '8px', border: 'none',
                  backgroundColor: activeTab === tab.id ? '#00E676' : 'transparent',
                  color: activeTab === tab.id ? '#000' : '#B0BEC5',
                  fontSize: '13px', fontWeight: activeTab === tab.id ? 700 : 400,
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Tab content */}
        {activeTab === 'propose' && (
          <ProposeTab
            matchId={matchId}
            onGuestProposed={onGuestAdded}
            onShowNotification={onShowNotification}
          />
        )}
        {activeTab === 'direct' && isOrganizer && (
          <DirectTab
            matchId={matchId}
            onGuestAdded={onGuestAdded}
            onShowNotification={onShowNotification}
          />
        )}
      </div>
    </div>
  );
};
